'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { Button } from '../ui';
import { Check } from 'lucide-react';
import SectionTitle from '../ui/SectionTitle';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  website: string;
  message: string;
}

interface FormState {
  isSubmitting: boolean;
  isSuccess: boolean;
  error: string | null;
  fieldErrors: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    website?: string;
    message?: string;
  };
}

interface ContactUsFormProps {
  isHomePage?: boolean;
}

const ContactUsForm = ({ isHomePage = false }: ContactUsFormProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    website: '',
    message: ''
  });

  const [phone, setPhone] = useState('');
  const [touchedFields, setTouchedFields] = useState<{ [key: string]: boolean }>({});
  const [sourcePage, setSourcePage] = useState<string>('');
  const [sourceType, setSourceType] = useState<'blog' | 'news-pr' | 'thought-leadership' | 'page' | null>(null);
  const [sourceSlug, setSourceSlug] = useState<string>('');

  // Determine source page information
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const currentPath = pathname || window.location.pathname;
      const fullUrl = window.location.href;

      setSourcePage(fullUrl);

      // Determine source type and slug based on pathname
      if (currentPath.startsWith('/blogs/')) {
        setSourceType('blog');
        const slug = currentPath.replace('/blogs/', '');
        setSourceSlug(slug);
      } else if (currentPath.startsWith('/news-pr/')) {
        setSourceType('news-pr');
        const slug = currentPath.replace('/news-pr/', '');
        setSourceSlug(slug);
      } else if (currentPath.startsWith('/thought-leadership/')) {
        setSourceType('thought-leadership');
        const slug = currentPath.replace('/thought-leadership/', '');
        setSourceSlug(slug);
      } else {
        setSourceType('page');
        setSourceSlug(currentPath);
      }
    }
  }, [pathname]);

  const [formState, setFormState] = useState<FormState>({
    isSubmitting: false,
    isSuccess: false,
    error: null,
    fieldErrors: {}
  });

  const validateField = (name: string, value: string, forceValidation = false): string | undefined => {
    // Only validate if field is touched or validation is forced (for form submission)
    if (!forceValidation && !touchedFields[name]) {
      return undefined;
    }

    switch (name) {
      case 'firstName':
      case 'lastName':
        return value.trim().length < 2 ? 'This field must be at least 2 characters' : undefined;
      case 'email': {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailRegex.test(value) ? 'Please enter a valid email address' : undefined;
      }
      case 'phone': {
        if (!value) return undefined;
        // Basic validation for international phone numbers
        const phoneRegex = /^\+[1-9]\d{1,14}$/;
        const cleaned = value.replace(/[\s\-\(\)]/g, '');
        return !phoneRegex.test(cleaned) ? 'Please enter a valid international phone number' : undefined;
      }
      case 'website': {
        if (!value) return undefined;

        const isOnlyNumbers = /^\d+$/.test(value);
        const isRandomString = /^[a-zA-Z0-9]+$/.test(value) && value.length < 3;

        if (isOnlyNumbers) {
          return 'Please enter a valid website URL, not just numbers';
        }

        if (isRandomString) {
          return 'Please enter a complete website URL';
        }

        const hasProtocol = /^https?:\/\//.test(value);
        const hasDomain = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}/.test(value);
        const hasValidPath = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}(\/.*)?$/.test(value);

        if (!hasProtocol && !hasDomain && !hasValidPath) {
          return 'Please enter a valid website URL (e.g., example.com or https://example.com)';
        }

        try {
          const toTest = /^(https?:)?\/\//.test(value) ? value : `https://${value}`;
          const url = new URL(toTest);

          if (!url.hostname.includes('.')) {
            return 'Please enter a valid domain name with extension (e.g., .com, .org)';
          }

          const tldPattern = /\.[a-zA-Z]{2,}$/;
          if (!tldPattern.test(url.hostname)) {
            return 'Please enter a valid domain with proper extension';
          }

          return undefined;
        } catch {
          return 'Please enter a valid website URL';
        }
      }
      case 'message':
        return value.trim().length < 10 ? 'Message must be at least 10 characters' : undefined;
      default:
        return undefined;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Mark field as touched
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));

    // Clear general error when user starts typing
    if (formState.error) {
      setFormState(prev => ({ ...prev, error: null }));
    }

    // Validate field and update field errors (only if touched)
    const fieldError = validateField(name, value, false);
    setFormState(prev => ({
      ...prev,
      fieldErrors: {
        ...prev.fieldErrors,
        [name]: fieldError
      }
    }));
  };

  const handlePhoneChange = (phone: string) => {
    setPhone(phone);
    setFormData(prev => ({
      ...prev,
      phone: phone
    }));

    // Mark phone field as touched
    setTouchedFields(prev => ({
      ...prev,
      phone: true
    }));

    // Clear general error when user starts typing
    if (formState.error) {
      setFormState(prev => ({ ...prev, error: null }));
    }

    // Validate phone field (only if touched)
    const fieldError = validateField('phone', phone, false);
    setFormState(prev => ({
      ...prev,
      fieldErrors: {
        ...prev.fieldErrors,
        ...(fieldError && { phone: fieldError })
      }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields before submission (force validation)
    const fieldErrors: { [key: string]: string } = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key as keyof FormData], true);
      if (error) {
        fieldErrors[key] = error;
      }
    });

    // Also validate phone field
    const phoneError = validateField('phone', phone, true);
    if (phoneError) {
      fieldErrors.phone = phoneError;
    }

    if (Object.keys(fieldErrors).length > 0) {
      setFormState(prev => ({
        ...prev,
        fieldErrors
      }));
      return;
    }

    setFormState({
      isSubmitting: true,
      isSuccess: false,
      error: null,
      fieldErrors: {}
    });

    try {
      // Create FormData object for API submission
      const submitData = new FormData();

      // Add form fields
      submitData.append('firstName', formData.firstName.trim());
      submitData.append('lastName', formData.lastName.trim());
      submitData.append('email', formData.email.trim());
      submitData.append('phone', phone.trim());
      submitData.append('message', formData.message.trim());
      submitData.append('website', formData.website.trim());
      submitData.append('services', '');
      submitData.append('nda', 'false');

      // Add source tracking fields
      if (sourcePage) {
        submitData.append('sourcePage', sourcePage);
      }
      if (sourceType) {
        submitData.append('sourceType', sourceType);
      }
      if (sourceSlug) {
        submitData.append('sourceSlug', sourceSlug);
      }

      const response = await fetch('/api/contact', {
        method: 'POST',
        body: submitData,
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Server returned invalid response. Please try again.');
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || 'Failed to submit form');
      }

      // Success → reset form and redirect to thank you page
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        website: '',
        message: ''
      });
      setPhone('');
      setTouchedFields({});
      setFormState({
        isSubmitting: false,
        isSuccess: true,
        error: null,
        fieldErrors: {}
      });

      router.push('/thankyou');
      return;

    } catch (error) {
      console.error('Form submission error:', error);
      setFormState({
        isSubmitting: false,
        isSuccess: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred',
        fieldErrors: {}
      });
    }
  };

  return (

    <section >

      <div className="us-version container mx-auto relative  overflow-hidden flex flex-col items-center justify-center ">
        <div className="custom-container mx-auto flex flex-col gap-5">
          <SectionTitle
            badge="Ready to Scale?"
            title="Scale Smarter with a Clear Paid Media Plan."
            description={"Whether you need a Google Ads agency, PPC management services, Meta ads optimization, CRO support, or a complete paid media agency partner, we will show you exactly where the opportunity is. Our free audit identifies wasted spend, tracking gaps, and your clearest 90-day roadmap."}
            align="center"
          />

          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
            {[
              "No credit card required",
              "Results in 48 hours",
              "No sales pressure",
              "100% confidential",
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-2 text-sm md:text-base font-medium text-gray-700"
              >
                <Check className="h-5 w-5 text-red-600 shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>


        </div>
      </div>
      <section
        className=" relative px-4">

        <div className={`max-w-6xl w-full bg-white shadow-[0_4px_54px_0_rgba(0,0,0,0.05)] lg:rounded-md mx-auto mt-4 sm:mt-8 lg:mt-10 ${isHomePage ? 'p-8' : 'p-4 sm:p-12 lg:p-16 xl:p-24'}`}>
          {/* Heading */}
          <div className="mb-4 sm:mb-8 lg:mb-10">
            <p className="text-gray-600 text-base sm:text-lg">Let&apos;s Talk Strategy </p>
            <h2 className={`  text-gray-900 mt-2 leading-tight tracking-tight ${isHomePage ? 'text-2xl !font-medium' : 'text-xl lg:text-2xl xl:text-4xl'}`}>
              Connect with our experts today to start your branding journey.
            </h2>
          </div>


          {/* Error Message */}
          {formState.error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Error submitting form
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{formState.error}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label htmlFor="firstName" className="sr-only">
                First Name
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleInputChange}
                className={`w-full border-b py-3 text-sm sm:text-base placeholder-gray-500 transition-colors duration-200 focus:outline-none ${formState.fieldErrors.firstName
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-gray-300 focus:border-black'
                  }`}
                placeholder="First Name"
                required
                aria-label="First Name"
              />
              {formState.fieldErrors.firstName && touchedFields.firstName && (
                <p className="mt-1 text-xs text-red-500 font-medium">
                  {formState.fieldErrors.firstName}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="lastName" className="sr-only">
                Last Name
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleInputChange}
                className={`w-full border-b py-3 text-sm sm:text-base placeholder-gray-500 transition-colors duration-200 focus:outline-none ${formState.fieldErrors.lastName
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-gray-300 focus:border-black'
                  }`}
                placeholder="Last Name"
                required
                aria-label="Last Name"
              />
              {formState.fieldErrors.lastName && touchedFields.lastName && (
                <p className="mt-1 text-xs text-red-500 font-medium">
                  {formState.fieldErrors.lastName}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="email" className="sr-only">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full border-b py-3 text-sm sm:text-base placeholder-gray-500 transition-colors duration-200 focus:outline-none ${formState.fieldErrors.email
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-gray-300 focus:border-black'
                  }`}
                placeholder="Email"
                required
                aria-label="Email Address"
              />
              {formState.fieldErrors.email && touchedFields.email && (
                <p className="mt-1 text-xs text-red-500 font-medium">
                  {formState.fieldErrors.email}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="phone" className="sr-only">
                Phone Number
              </label>
              <PhoneInput
                value={phone}
                onChange={handlePhoneChange}
                defaultCountry="in"
                className={`w-full ${formState.fieldErrors.phone
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-gray-300 focus:border-black'
                  }`}
                inputStyle={{
                  border: 'none',
                  borderBottom: formState.fieldErrors.phone ? '1px solid #fca5a5' : '1px solid #d1d5db',
                  borderRadius: '0',
                  padding: '12px 0',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  width: '100%'
                }}
                countrySelectorStyleProps={{
                  buttonStyle: {
                    border: 'none',
                    borderBottom: formState.fieldErrors.phone ? '1px solid #fca5a5' : '1px solid #d1d5db',
                    borderRadius: '0',
                    padding: '12px 0',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    backgroundColor: 'transparent'
                  }
                }}
                placeholder="Phone Number"
              />
              {formState.fieldErrors.phone && touchedFields.phone && (
                <p className="mt-1 text-xs text-red-500 font-medium">
                  {formState.fieldErrors.phone}
                </p>
              )}
            </div>
            <div className="md:col-span-2">
              <label htmlFor="website" className="sr-only">
                Website Link
              </label>
              <input
                id="website"
                name="website"
                type="text"
                value={formData.website}
                onChange={handleInputChange}
                className={`w-full border-b py-3 text-sm sm:text-base placeholder-gray-500 transition-colors duration-200 focus:outline-none ${formState.fieldErrors.website
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-gray-300 focus:border-black'
                  }`}
                placeholder="Website Link"
                aria-label="Website Link"
              />
              {formState.fieldErrors.website && touchedFields.website && (
                <p className="mt-1 text-xs text-red-500 font-medium">
                  {formState.fieldErrors.website}
                </p>
              )}
            </div>
            <div className="md:col-span-2">
              <label htmlFor="message" className="sr-only">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                className={`w-full border-b py-3 text-sm sm:text-base placeholder-gray-500 transition-colors duration-200 resize-vertical min-h-[100px] focus:outline-none ${formState.fieldErrors.message
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-gray-300 focus:border-black'
                  }`}
                placeholder="Message"
                required
                aria-label="Message"
              />
              {formState.fieldErrors.message && touchedFields.message && (
                <p className="mt-1 text-xs text-red-500 font-medium">
                  {formState.fieldErrors.message}
                </p>
              )}
            </div>
            <div className="flex justify-start mt-6 sm:mt-8">
              <Button
                type="submit"
                responsiveSize={{
                  mobile: 'md',
                  desktop: 'lg'
                }}
                variant="primary"
                className="w-full sm:w-auto"
                disabled={formState.isSubmitting}
              >
                {formState.isSubmitting ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-30" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-100" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </div>
                ) : (
                  'Contact Us Today'
                )}
              </Button>


            </div>

          </form>
        </div>
      </section>
    </section>

  );
};

export default ContactUsForm;